import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getPasswordResetErrorMessage } from "@/lib/utils/stytch-errors";

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onResetPassword: (email: string) => void;
}

export function ForgotPasswordModal({
  open,
  onClose,
  onResetPassword,
}: ForgotPasswordModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handleResetPassword = async () => {
    setLoading(true);
    try {
      await onResetPassword(email);
      setError(null);
      setEmailSent(true);
    } catch (error: unknown) {
      console.error("Error resetting password:", error);
      setError(getPasswordResetErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
          setError(null);
          setEmailSent(false);
          setEmail("");
        }
      }}
    >
      <DialogContent className="w-full max-w-[579px] gap-0 rounded-[24px] p-0">
        <div className="relative px-10 pb-10 pt-12">
          <img
            src="assets/verification-icon.png"
            alt="Icon"
            className="mb-8 h-16 w-16"
          />

          <DialogHeader className="space-y-3 text-left">
            <DialogTitle className="text-[20px] font-medium tracking-tight">
              Forgot your password
            </DialogTitle>
            <DialogDescription className="max-w-[680px] text-[16px] leading-snug text-[#9a8f80]">
              Enter the email address you registered with and we&apos;ll send you
              instructions to reset your password
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="mt-6 rounded-[12px] bg-red-200 p-4 text-sm font-medium text-red-500">
              {error}
            </div>
          )}

          <div className="mt-10">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="h-16 rounded-[12px] border-[#d9d2c7] bg-[#f3efe9] px-6 text-[16px] placeholder:text-[#9a8f80] focus-visible:ring-2 focus-visible:ring-black/5"
            />
          </div>

          <div className="mt-10 h-px w-full bg-[#e7e0d6]" />

          {emailSent ? (
            <p className="mt-6 text-base text-[#9a8f80]">
              If an account exists with this email, you will receive an email
              with a link to reset your password. Password reset link will
              expire in 15 minutes.
            </p>
          ) : (
            <Button
              className="mt-8 h-16 w-full rounded-[12px] bg-black text-lg font-semibold text-white hover:bg-black/90"
              onClick={handleResetPassword}
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send instructions
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
